// components/wallet/TokenEconomyVisualization.jsx
// Updated: Shows PARTICIPATION-BASED loyalty tiers (not cart-based)
// Tiers unlock based on purchases + referrals, NOT cart size
// Activity resets expiration clock

import React from 'react';
import { 
  ArrowRight, 
  TrendingUp, 
  Users, 
  Award, 
  Gift, 
  ShoppingCart, 
  Share2, 
  Coins,
  MapPin,
  Zap,
  Star,
  Crown,
  Shield,
  Clock,
  RefreshCw
} from 'lucide-react';
import { 
  TOKEN_CONFIG, 
  formatTokens, 
  formatTokenValue,
  getShareEarnDisplay 
} from '../../utils/tokenEconomy';

const TokenEconomyVisualization = () => {
  const shareEarnInfo = getShareEarnDisplay();
  
  // Loyalty tier display data - HYBRID: 40% OR tier cap
  const loyaltyTiers = [
    {
      tier: 'NEW_USER',
      label: 'New User',
      icon: Star,
      color: 'gray',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      requirements: 'Just joined',
      maxRedemption: '40% / $15 cap',
      expiration: '90 days inactivity',
      benefitMessage: '"Welcome!"'
    },
    {
      tier: 'BRONZE',
      label: 'Bronze',
      icon: Shield,
      color: 'amber',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700',
      requirements: '5+ purchases',
      maxRedemption: '40% / $25 cap',
      expiration: '180 days inactivity',
      benefitMessage: '"You\'re getting started!"'
    },
    {
      tier: 'SILVER',
      label: 'Silver',
      icon: Award,
      color: 'slate',
      bgColor: 'bg-slate-200',
      textColor: 'text-slate-700',
      requirements: '15+ purchases, 2+ referrals',
      maxRedemption: '40% / $40 cap',
      expiration: '1 year inactivity',
      benefitMessage: '"You\'re a regular!"'
    },
    {
      tier: 'GOLD',
      label: 'Gold',
      icon: Crown,
      color: 'yellow',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-700',
      requirements: '30+ purchases, 5+ referrals',
      maxRedemption: '40% / $75 cap',
      expiration: '2 years inactivity',
      benefitMessage: '"You\'re a power user!"'
    },
    {
      tier: 'PLATINUM',
      label: 'Platinum',
      icon: Zap,
      color: 'purple',
      bgColor: 'bg-gradient-to-r from-purple-100 to-indigo-100',
      textColor: 'text-purple-700',
      requirements: '50+ purchases, 10+ referrals',
      maxRedemption: 'UNCAPPED 40%',
      expiration: 'NEVER',
      benefitMessage: '"You\'re family!"'
    }
  ];
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-4">
        <Coins className="w-6 h-6 text-yellow-500" />
        <h3 className="text-lg font-semibold">How Tokens Work</h3>
      </div>
      
      <div className="space-y-6">
        {/* Token Value */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4">
          <h4 className="font-medium text-yellow-900 mb-2">Token Value</h4>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">1</p>
              <p className="text-xs text-gray-600">token</p>
            </div>
            <ArrowRight className="text-yellow-400" size={24} />
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">${TOKEN_CONFIG.VALUE_PER_TOKEN.toFixed(2)}</p>
              <p className="text-xs text-gray-600">value</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Tokens can be used for discounts on purchases, transferred to friends, or converted to vendor credit.
          </p>
        </div>

        {/* Token Flow Diagram */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-3">Token Flow</h4>
          <div className="flex items-center justify-between text-sm">
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                <Users size={20} />
              </div>
              <p className="text-xs">You Shop</p>
            </div>
            <ArrowRight className="text-blue-400" size={20} />
            <div className="text-center">
              <div className="bg-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                <span className="text-xs font-bold">7.5%</span>
              </div>
              <p className="text-xs">Platform Fee</p>
            </div>
            <ArrowRight className="text-blue-400" size={20} />
            <div className="text-center">
              <div className="bg-yellow-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                <Coins size={20} />
              </div>
              <p className="text-xs">Token Pool</p>
            </div>
            <ArrowRight className="text-blue-400" size={20} />
            <div className="text-center">
              <div className="bg-purple-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                <Award size={20} />
              </div>
              <p className="text-xs">Your Rewards</p>
            </div>
          </div>
        </div>

        {/* Ways to Earn */}
        <div>
          <h4 className="font-medium mb-3">Ways to Earn Tokens</h4>
          <div className="space-y-3">
            {/* Purchase Tokens */}
            <div className="flex items-start p-3 bg-gray-50 rounded-lg">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <ShoppingCart className="text-blue-600" size={18} />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">Make Purchases</p>
                    <p className="text-sm text-gray-600">Earn tokens on every order</p>
                  </div>
                  <span className="text-sm font-bold text-blue-600">
                    +{TOKEN_CONFIG.BASE_PURCHASE_TOKENS}/item
                  </span>
                </div>
              </div>
            </div>
            
            {/* Bulk Bonus */}
            <div className="flex items-start p-3 bg-gray-50 rounded-lg">
              <div className="bg-orange-100 rounded-full p-2 mr-3">
                <Zap className="text-orange-600" size={18} />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">Bulk Hero Bonus</p>
                    <p className="text-sm text-gray-600">Buy 3+ items: +25% | Buy 5+ items: +50%</p>
                  </div>
                  <span className="text-sm font-bold text-orange-600">
                    +25-50%
                  </span>
                </div>
              </div>
            </div>

            {/* Referral Rewards */}
            <div className="flex items-start p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="bg-purple-100 rounded-full p-2 mr-3">
                <Share2 className="text-purple-600" size={18} />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-purple-800">Share & Earn (Recurring!)</p>
                    <p className="text-sm text-purple-600">Earn every time someone buys with your link</p>
                  </div>
                  <span className="text-sm font-bold text-purple-600">
                    +{shareEarnInfo.sharerTokens}/referral
                  </span>
                </div>
              </div>
            </div>
            
            {/* Friend Bonus */}
            <div className="flex items-start p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="bg-green-100 rounded-full p-2 mr-3">
                <Gift className="text-green-600" size={18} />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-green-800">Use a Friend's Link</p>
                    <p className="text-sm text-green-600">Get bonus tokens on your purchase</p>
                  </div>
                  <span className="text-sm font-bold text-green-600">
                    +{TOKEN_CONFIG.REFERRAL_LINK_BONUS + TOKEN_CONFIG.FIRST_PURCHASE_BONUS}
                  </span>
                </div>
              </div>
            </div>

            {/* Pickup Bonus */}
            <div className="flex items-start p-3 bg-gray-50 rounded-lg">
              <div className="bg-teal-100 rounded-full p-2 mr-3">
                <MapPin className="text-teal-600" size={18} />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">Choose Pickup</p>
                    <p className="text-sm text-gray-600">Earn extra tokens for pickup orders</p>
                  </div>
                  <span className="text-sm font-bold text-teal-600">
                    +{TOKEN_CONFIG.PICKUP_BONUS}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* LOYALTY TIERS - HYBRID System: 40% OR Tier Cap */}
        <div>
          <h4 className="font-medium mb-2">Loyalty Tiers - Unlock Higher Redemption</h4>
          
          {/* Hybrid System Explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> You can redeem up to <strong>40% of your cart</strong> in tokens, 
              but your tier sets a <strong>maximum dollar cap</strong> until you reach Platinum.
            </p>
          </div>
          
          {/* Expiration Reset Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <RefreshCw className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-700">
              <strong>Activity resets your clock!</strong> Every purchase, referral, or platform activity 
              resets your token expiration timer back to full.
            </p>
          </div>
          
          <div className="space-y-3">
            {loyaltyTiers.map((tier) => {
              const Icon = tier.icon;
              return (
                <div 
                  key={tier.tier} 
                  className={`${tier.bgColor} rounded-lg p-4 border ${tier.tier === 'PLATINUM' ? 'border-purple-300' : 'border-transparent'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tier.tier === 'PLATINUM' ? 'bg-purple-600 text-white' : 'bg-white'}`}>
                        <Icon className={tier.tier === 'PLATINUM' ? 'text-white' : tier.textColor} size={20} />
                      </div>
                      <div>
                        <p className={`font-bold ${tier.textColor}`}>{tier.label}</p>
                        <p className="text-xs text-gray-600">{tier.requirements}</p>
                        <p className="text-xs text-gray-500 italic">{tier.benefitMessage}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${tier.tier === 'PLATINUM' ? 'text-purple-600' : 'text-gray-800'}`}>
                        {tier.maxRedemption}
                      </p>
                      <p className="text-xs text-gray-500">per order</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                    <Clock size={12} />
                    <span>Expires after: {tier.expiration}</span>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Example Table */}
          <div className="mt-4 bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-medium text-gray-700 mb-2">Example: $100 Cart (40% = $40 potential)</p>
            <div className="grid grid-cols-5 gap-1 text-xs text-center">
              <div className="bg-white rounded p-1">
                <p className="text-gray-500">New</p>
                <p className="font-bold text-gray-700">$15</p>
              </div>
              <div className="bg-white rounded p-1">
                <p className="text-amber-600">Bronze</p>
                <p className="font-bold text-gray-700">$25</p>
              </div>
              <div className="bg-white rounded p-1">
                <p className="text-slate-600">Silver</p>
                <p className="font-bold text-gray-700">$40</p>
              </div>
              <div className="bg-white rounded p-1">
                <p className="text-yellow-600">Gold</p>
                <p className="font-bold text-gray-700">$40</p>
              </div>
              <div className="bg-purple-100 rounded p-1">
                <p className="text-purple-600">Platinum</p>
                <p className="font-bold text-purple-700">$40</p>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-3 text-center">
            💡 Platinum members enjoy UNCAPPED 40% redemption on ANY cart size!
          </p>
        </div>

        {/* Share & Earn Highlight */}
        <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="text-purple-600" size={20} />
            <h4 className="font-medium text-purple-800">Share & Earn is Recurring!</h4>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Unlike one-time referral bonuses, UniLedge rewards you <strong>every single time</strong> someone uses your link to make a purchase.
            Plus, referrals count toward your loyalty tier!
          </p>
          <div className="flex gap-4 justify-center">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{shareEarnInfo.sharerTokens}</p>
              <p className="text-xs text-gray-600">tokens per referral</p>
              <p className="text-xs text-purple-600">{shareEarnInfo.sharerValue}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{shareEarnInfo.newUserTokens || shareEarnInfo.friendTokens}</p>
              <p className="text-xs text-gray-600">tokens for friend</p>
              <p className="text-xs text-green-600">{shareEarnInfo.newUserValue || shareEarnInfo.friendValue}</p>
            </div>
          </div>
        </div>

        {/* Example Calculation */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-2">Example: Your First Order</h4>
          <p className="text-sm text-gray-600 mb-3">
            Buy 5 items using a friend's referral link:
          </p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Base tokens (5 items × 10)</span>
              <span className="font-medium">50 tokens</span>
            </div>
            <div className="flex justify-between">
              <span>Bulk bonus (5+ items = +50%)</span>
              <span className="font-medium text-orange-600">+25 tokens</span>
            </div>
            <div className="flex justify-between">
              <span>Referral link bonus</span>
              <span className="font-medium text-green-600">+{TOKEN_CONFIG.REFERRAL_LINK_BONUS} tokens</span>
            </div>
            <div className="flex justify-between">
              <span>First purchase bonus</span>
              <span className="font-medium text-blue-600">+{TOKEN_CONFIG.FIRST_PURCHASE_BONUS} tokens</span>
            </div>
            <div className="flex justify-between pt-2 border-t font-bold">
              <span>Total earned</span>
              <span className="text-yellow-600">110 tokens ({formatTokenValue(110)})</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenEconomyVisualization;